import { Box, Divider, TextField, Button as UIButton } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import clsx from "clsx";
import { useEffect, useState, useMemo } from "react";
import * as XLSX from "xlsx";
import { createUseStyles } from "$utils/createUseStyles";
import { useQuery } from "@tanstack/react-query";
import Edit2LineIcon from "remixicon-react/Edit2LineIcon";
import Upload2LineIcon from "remixicon-react/Upload2LineIcon";
import { theme } from "$utils/theme/bataillog";
import { formatDateTime } from "$utils/utils";
import { VFSOLUTIONS } from "$utils/constants";
import ParcelPopin from "$containers/Parcel/ParcelPopin";
import { EDB_IS_DISABLED_MSG } from "$containers/EDB/constants";
import { Badge, Button, InputGrpNumber, Modal, Table, Text } from "$components";
import {
  CompanyType,
  DispositionType,
  PreparationOrder,
  PreparationOrderStatus,
  Shipment,
  ShipmentStatus,
} from "$types";
import JTooltip from "$components/JTooltip/JTooltip";
import ParcelTable from "$containers/Parcel/ParcelTable";
import AddressSelect from "$forms/AddressSelect";
import WarehouseSelect from "$forms/WarehouseSelect";
import useAuth from "$hooks/useAuth";
import useParcel from "$hooks/useParcel";
import { EntrepotService, ShippingRecordService } from "$services";
import { downloadFile } from "$components/Export/utils";
import { Loader } from "$components/Layout/Loader";
import CompanySelect from "$forms/CompanySelect";
import useExport from "$hooks/useExport";
import useJStyles from "$hooks/useJStyles";
import useCompanyOptions from "../ShippingRecord/useCompanyOptions";
import PopperButtons from "./PopperButtons";
import { CacheInvalidationAction } from "$utils/cache";
import WarehouseSelector from "$components/JSelectors/WarehouseSelector/WarehouseSelector";

const IS_OP_IN_SHIPPED_SHIPMENT_MSG = (shipmentRef: string) =>
  `Cette Référence Préparation Entrepôt n'est plus modifiable car l'OP est embarqué dans le dossier [${shipmentRef}].`;

const IS_OP_IN_DOSSIER_CANCEL_MSG = (dossierCode: string) =>
  `Cet OP ne peut pas être annulé car il est embarqué dans le dossier ${dossierCode}`;

const getDossierCodeFromShippingPayload = (shippingData: unknown) => {
  if (!shippingData || typeof shippingData !== "object") return undefined;
  const data = shippingData as {
    code?: string;
    shippingRecord?: { code?: string };
  };
  return data.shippingRecord?.code || data.code || undefined;
};

const EXPORT_OP_XLS_HEAD: string[] = [
  "entrepôt de préparation",
  "N° OP",
  "ref secondaire",
  "référence produit",
  "désignation",
  "quantité à préparer",
];

type FormProperties = {
  value: string;
  error: string;
  isRequired: boolean;
  isDisabled: boolean;
};

type ContactShipping = {
  adress: FormProperties;
  city: FormProperties;
  colisage: FormProperties;
  complementAdress: FormProperties;
  country: FormProperties;
  postalCode: FormProperties;
  shipEntrepotName: FormProperties;
  shipPlace?: FormProperties;
};

type ShippingAddress = {
  adress?: string;
  city?: string;
  complementAdress?: string;
  country?: string;
  postalCode?: string;
};

type ContactShippingValue = {
  address: ShippingAddress;
  colisage: string;
  shipEntrepotName: string;
  shipPlace?: string;
};

type OPDeliveryDataType = {
  otherPlace: ContactShippingValue;
  subcontratedWarehouse: ContactShippingValue;
  nearestWarehouse: ContactShippingValue;
};

const defaultOPDeliveryData: OPDeliveryDataType = {
  otherPlace: {
    address: {
      adress: "",
      city: "",
      complementAdress: "",
      country: "",
      postalCode: "",
    },
    colisage: "",
    shipEntrepotName: "",
    shipPlace: "",
  },
  subcontratedWarehouse: {
    address: {
      adress: "",
      city: "",
      complementAdress: "",
      country: "",
      postalCode: "",
    },
    colisage: "",
    shipEntrepotName: "",
    shipPlace: "",
  },
  nearestWarehouse: {
    address: {
      adress: "",
      city: "",
      complementAdress: "",
      country: "",
      postalCode: "",
    },
    colisage: "",
    shipEntrepotName: "",
    shipPlace: "",
  },
};

type Props = {
  op: PreparationOrder;
  handleChangeProductOP: (
    indexOP: number,
    indexProduct: number,
    productQty: number,
    entrepot: any
  ) => void;
  handleChangeOP: (
    indexOP: number,
    op: any,
    sendData: boolean,
    refreshStatusEdb?: boolean
  ) => void;
  isDisabled: boolean;
  onCancelOP: (idOp: string) => void;
  // on cancel OPItems
  handleCancelOPItems: (idOPItems: string, indexOP: number) => void;
  onDeleteItemFromEdb: (idProduct: string, idEdb: string) => void;
  indexOP: number;
  edb: any;
  opList: any[];
};

function OP({
  opList,
  op,
  edb,
  handleChangeProductOP,
  handleChangeOP,
  indexOP,
  isDisabled,
  onCancelOP,
  // for cancel OP Items
  handleCancelOPItems,
  onDeleteItemFromEdb,
}: Props) {
  const {
    code,
    reference,
    refPreparationEntrepot,
    entrepot,
    products,
    status,
    _id,
    shipEntrepotName,
    colisage,

    parcel: initialParcel,
    shippingRecord: initialShippingRecord,
    shippingRecordId,
  } = op;

  // Hooks doivent être au top level du composant
  const classes = useStyles();
  const { jclasses } = useJStyles();

  const { exportData } = useExport();

  // TODO: to edit when main API handles parcel id
  const { parcel, createParcel, updateParcel, deleteParcel } = useParcel(
    op._id,
    initialParcel
  );
  const { token } = useAuth();

  // Use the correct type for the state
  const [OpInAShippedShipment, setOpInAShippedShipment] =
    useState<Shipment | null>(null);

  // Initialize the shipping record state with data from the gateway
  useEffect(() => {
    if (initialShippingRecord) {
      // Need to cast to handle the type mismatch between ShippingRecord and Shipment
      const shipmentData = initialShippingRecord as unknown as Shipment;
      setOpInAShippedShipment(shipmentData);
    }
  }, [initialShippingRecord]);

  const dossierCodeFromPayload = useMemo(
    () =>
      getDossierCodeFromShippingPayload(initialShippingRecord) ||
      getDossierCodeFromShippingPayload(OpInAShippedShipment),
    [initialShippingRecord, OpInAShippedShipment]
  );

  const isOpInDossier = Boolean(
    (shippingRecordId && shippingRecordId !== "") || dossierCodeFromPayload
  );

  const { data: fetchedShippingRecord } = useQuery({
    queryKey: [CacheInvalidationAction.GET_SHIPPING_RECORD, shippingRecordId],
    queryFn: () =>
      ShippingRecordService.getShippingRecord(
        token,
        shippingRecordId as string
      ),
    enabled: Boolean(
      token &&
      shippingRecordId &&
      shippingRecordId !== "" &&
      !dossierCodeFromPayload
    ),
    staleTime: 5 * 60 * 1000,
  });

  const dossierCode =
    dossierCodeFromPayload ||
    (fetchedShippingRecord && !fetchedShippingRecord.error
      ? fetchedShippingRecord.code
      : undefined);

  const cancelOpDisabled = isDisabled || isOpInDossier;
  const cancelOpTooltipTitle = isDisabled
    ? EDB_IS_DISABLED_MSG
    : isOpInDossier && dossierCode
      ? IS_OP_IN_DOSSIER_CANCEL_MSG(dossierCode)
      : isOpInDossier
        ? "Cet OP ne peut pas être annulé car il est embarqué dans un dossier"
        : false;

  const [showParcelDetails, toggleParcelDetails] = useState<boolean>(false);

  const [openPopinCancelOp, setOpenPopinCancelOp] = useState(false);
  const [openPopinValidateOp, setOpenPopinValidateOp] = useState(false);
  const [openPopinDeleteProduct, setOpenPopinDeleteProduct] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isParcelDrawerOpen, toggleParcelDrawer] = useState(false);
  const [editEntrepot, setEditEntrepot] = useState(false);
  const [refEntrepotValue, setRefEntrepotValue] = useState(
    refPreparationEntrepot
  );
  const [infosDeleteProductEdb, setInfosDeleteProductEdb] = useState({
    idOpItem: "",
    idProduct: "",
  });
  const [shipType, setShipType] = useState<string>(
    op?.shipType ? op?.shipType : edb?.dispositionType
  );
  const { relayAgencyOptions } = useCompanyOptions();

  const [OPDeliveryData, setOPDeliveryData] = useState<OPDeliveryDataType>(
    defaultOPDeliveryData
  );

  const [contactShipping, setContactShipping] = useState<ContactShipping>({
    shipEntrepotName: {
      value: "",
      error: "",
      isRequired: false,
      isDisabled: false,
    },
    adress: {
      value: "",
      error: "",
      isRequired: true,
      isDisabled: false,
    },
    complementAdress: {
      value: "",
      error: "",
      isRequired: false,
      isDisabled: false,
    },
    postalCode: {
      value: "",
      error: "",
      isRequired: true,
      isDisabled: false,
    },
    city: {
      value: "",
      error: "",
      isRequired: true,
      isDisabled: false,
    },
    country: {
      value: "France",
      error: "",
      isRequired: true,
      isDisabled: false,
    },
    colisage: {
      value: "",
      error: "",
      isRequired: false,
      isDisabled: false,
    },
  });

  // Initialize OPDeliveryData from persisted OP JSON fields (nearest/other/subcontractor warehouses)
  useEffect(() => {
    const {
      nearestShipWarehouse,
      otherShipWarehouse,
      subcontractorShipWarehouse,
    } = op as any;

    if (
      !nearestShipWarehouse &&
      !otherShipWarehouse &&
      !subcontractorShipWarehouse
    ) {
      return;
    }

    setOPDeliveryData((prev) => ({
      otherPlace: {
        address: {
          adress: otherShipWarehouse?.adress || prev.otherPlace.address.adress,
          city: otherShipWarehouse?.city || prev.otherPlace.address.city,
          complementAdress:
            otherShipWarehouse?.complementAdress ||
            prev.otherPlace.address.complementAdress,
          country:
            otherShipWarehouse?.country || prev.otherPlace.address.country,
          postalCode:
            otherShipWarehouse?.postalCode ||
            prev.otherPlace.address.postalCode,
        },
        colisage: prev.otherPlace.colisage,
        shipEntrepotName:
          otherShipWarehouse?.name || prev.otherPlace.shipEntrepotName,
        shipPlace: otherShipWarehouse?.place || prev.otherPlace.shipPlace,
      },
      subcontratedWarehouse: {
        address: {
          adress:
            subcontractorShipWarehouse?.adress ||
            prev.subcontratedWarehouse.address.adress,
          city:
            subcontractorShipWarehouse?.city ||
            prev.subcontratedWarehouse.address.city,
          complementAdress:
            subcontractorShipWarehouse?.complementAdress ||
            prev.subcontratedWarehouse.address.complementAdress,
          country:
            subcontractorShipWarehouse?.country ||
            prev.subcontratedWarehouse.address.country,
          postalCode:
            subcontractorShipWarehouse?.postalCode ||
            prev.subcontratedWarehouse.address.postalCode,
        },
        colisage: prev.subcontratedWarehouse.colisage,
        shipEntrepotName:
          subcontractorShipWarehouse?.name ||
          prev.subcontratedWarehouse.shipEntrepotName,
        shipPlace:
          subcontractorShipWarehouse?.place ||
          prev.subcontratedWarehouse.shipPlace,
      },
      nearestWarehouse: {
        address: {
          adress:
            nearestShipWarehouse?.adress ||
            prev.nearestWarehouse.address.adress,
          city:
            nearestShipWarehouse?.city || prev.nearestWarehouse.address.city,
          complementAdress:
            nearestShipWarehouse?.complementAdress ||
            prev.nearestWarehouse.address.complementAdress,
          country:
            nearestShipWarehouse?.country ||
            prev.nearestWarehouse.address.country,
          postalCode:
            nearestShipWarehouse?.postalCode ||
            prev.nearestWarehouse.address.postalCode,
        },
        colisage: prev.nearestWarehouse.colisage,
        shipEntrepotName:
          nearestShipWarehouse?.name || prev.nearestWarehouse.shipEntrepotName,
        shipPlace:
          nearestShipWarehouse?.place || prev.nearestWarehouse.shipPlace,
      },
    }));
  }, [op]);

  // Determine company filter based on edit mode or not
  // In edit mode: use EDB company and intervenantCompany
  // Not in edit mode: use dockyard company (original behavior)
  const companyFilter = useMemo(() => {
    const edbCompanyId = edb?.company?._id || edb?.company;
    const intervenantCompanyId =
      edb?.intervenantCompany?._id || edb?.intervenantCompany;
    const dockyardCompanyId =
      edb?.dockyard?.company?._id || edb?.dockyard?.company;

    if (!isDisabled) {
      const companyIds = [];
      if (edbCompanyId) {
        companyIds.push(edbCompanyId);
      }
      if (intervenantCompanyId) {
        companyIds.push(intervenantCompanyId);
      }
      return companyIds.length > 0 ? { $inCompany: companyIds } : {};
    }
    return dockyardCompanyId ? { company: dockyardCompanyId } : {};
  }, [
    isDisabled,
    edb?.company,
    edb?.intervenantCompany,
    edb?.dockyard?.company,
  ]);

  const isQueryEnabled = useMemo(() => {
    return Object.keys(companyFilter).length > 0;
  }, [companyFilter]);

  const { data: warehouses, isLoading: warehouseLoading } = useQuery({
    queryKey: [CacheInvalidationAction.GET_ENTREPOTS, companyFilter],
    queryFn: () => EntrepotService.getEntrepots(companyFilter),
    enabled: isQueryEnabled,
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    switch (shipType) {
      case DispositionType.NEAREST_WAREHOUSE:
        setContactShipping({
          shipEntrepotName: {
            value:
              (op?.shipType === shipType && op?.shipEntrepotName) ||
              OPDeliveryData.nearestWarehouse.shipEntrepotName ||
              "",
            error: "",
            isRequired: true,
            isDisabled: false,
          },
          adress: {
            value:
              (op?.shipType === shipType && op?.shipAdress) ||
              OPDeliveryData.nearestWarehouse.address.adress ||
              "",
            error: "",
            isRequired: true,
            isDisabled: false,
          },
          complementAdress: {
            value:
              (op?.shipType === shipType && op?.shipAdress2) ||
              OPDeliveryData.nearestWarehouse.address.complementAdress ||
              "",
            error: "",
            isRequired: false,
            isDisabled: false,
          },
          postalCode: {
            value:
              (op?.shipType === shipType && op?.shipPostalCode) ||
              OPDeliveryData.nearestWarehouse.address.postalCode ||
              "",
            error: "",
            isRequired: true,
            isDisabled: false,
          },
          city: {
            value:
              (op?.shipType === shipType && op?.shipCity) ||
              OPDeliveryData.nearestWarehouse.address.city ||
              "",
            error: "",
            isRequired: true,
            isDisabled: false,
          },
          country: {
            value:
              (op?.shipType === shipType && op?.shipCountry) ||
              OPDeliveryData.nearestWarehouse.address.country ||
              "France",
            error: "",
            isRequired: true,
            isDisabled: false,
          },
          colisage: {
            value:
              (op?.shipType === shipType && op?.colisage) ||
              colisage ||
              OPDeliveryData.nearestWarehouse.colisage ||
              "",
            error: "",
            isRequired: false,
            isDisabled: false,
          },
        });
        break;
      case DispositionType.SUBCONTRACTED_WAREHOUSE:
        setContactShipping({
          shipEntrepotName: {
            value:
              (op?.shipType === shipType && op?.shipEntrepotName) ||
              OPDeliveryData.subcontratedWarehouse.shipEntrepotName ||
              (edb?.dispositionType === shipType && edb?.entrepotName) ||
              "",
            error: "",
            isRequired: false,
            isDisabled: false,
          },
          adress: {
            value:
              (op?.shipType === shipType && op?.shipAdress) ||
              OPDeliveryData.subcontratedWarehouse.address.adress ||
              (edb?.dispositionType === shipType && edb?.dispositionAdress) ||
              "",
            error: "",
            isRequired: true,
            isDisabled: false,
          },
          complementAdress: {
            value:
              (op?.shipType === shipType && op?.shipAdress2) ||
              OPDeliveryData.subcontratedWarehouse.address.complementAdress ||
              (edb?.dispositionType === shipType && edb?.dispositionAdress2) ||
              "",
            error: "",
            isRequired: false,
            isDisabled: false,
          },
          postalCode: {
            value:
              (op?.shipType === shipType && op?.shipPostalCode) ||
              OPDeliveryData.subcontratedWarehouse.address.postalCode ||
              (edb?.dispositionType === shipType &&
                edb?.dispositionPostalCode) ||
              "",
            error: "",
            isRequired: true,
            isDisabled: false,
          },
          city: {
            value:
              (op?.shipType === shipType && op?.shipCity) ||
              OPDeliveryData.subcontratedWarehouse.address.city ||
              (edb?.dispositionType === shipType && edb?.dispositionCity) ||
              "",
            error: "",
            isRequired: true,
            isDisabled: false,
          },
          country: {
            value:
              (op?.shipType === shipType && op?.shipCountry) ||
              OPDeliveryData.subcontratedWarehouse.address.country ||
              (edb?.dispositionType === shipType && edb?.dispositionCountry) ||
              "France",
            error: "",
            isRequired: true,
            isDisabled: false,
          },
          colisage: {
            value:
              (op?.shipType === shipType && op?.colisage) ||
              colisage ||
              OPDeliveryData.subcontratedWarehouse.colisage ||
              "",
            error: "",
            isRequired: false,
            isDisabled: false,
          },
        });
        break;
      case DispositionType.OTHER_PLACE:
        setContactShipping({
          shipEntrepotName: {
            value: "",
            error: "",
            isRequired: false,
            isDisabled: false,
          },
          shipPlace: {
            value:
              OPDeliveryData.otherPlace.shipPlace ||
              (op?.shipType === shipType && op?.shipPlace) ||
              (edb?.dispositionType === shipType && edb?.dispositionPlace) ||
              "",
            error: "",
            isRequired: true,
            isDisabled: false,
          },
          adress: {
            value:
              (op?.shipType === shipType && op?.shipAdress) ||
              OPDeliveryData.otherPlace.address.adress ||
              (edb?.dispositionType === shipType && edb?.dispositionAdress) ||
              "",
            error: "",
            isRequired: true,
            isDisabled: false,
          },
          complementAdress: {
            value:
              (op?.shipType === shipType && op?.shipAdress2) ||
              OPDeliveryData.otherPlace.address.complementAdress ||
              (edb?.dispositionType === shipType && edb?.dispositionAdress2) ||
              "",
            error: "",
            isRequired: false,
            isDisabled: false,
          },
          postalCode: {
            value:
              (op?.shipType === shipType && op?.shipPostalCode) ||
              OPDeliveryData.otherPlace.address.postalCode ||
              (edb?.dispositionType === shipType &&
                edb?.dispositionPostalCode) ||
              "",
            error: "",
            isRequired: true,
            isDisabled: false,
          },
          city: {
            value:
              (op?.shipType === shipType && op?.shipCity) ||
              OPDeliveryData.otherPlace.address.city ||
              (edb?.dispositionType === shipType && edb?.dispositionCity) ||
              "",
            error: "",
            isRequired: true,
            isDisabled: false,
          },
          country: {
            value:
              (op?.shipType === shipType && op?.shipCountry) ||
              OPDeliveryData.otherPlace.address.country ||
              (edb?.dispositionType === shipType && edb?.dispositionCountry) ||
              "France",
            error: "",
            isRequired: true,
            isDisabled: false,
          },
          colisage: {
            value:
              (op?.shipType === shipType && op?.colisage) ||
              colisage ||
              OPDeliveryData.otherPlace.colisage ||
              "",
            error: "",
            isRequired: false,
            isDisabled: false,
          },
        });
        break;
      case DispositionType.COLLECTED:
        setContactShipping({
          shipEntrepotName: {
            value: entrepot?.name,
            error: "",
            isRequired: false,
            isDisabled: false,
          },
          shipPlace: {
            value: entrepot?.location?.adress,
            error: "",
            isRequired: true,
            isDisabled: false,
          },
          adress: {
            value: entrepot?.location?.adress,
            error: "",
            isRequired: true,
            isDisabled: false,
          },
          complementAdress: {
            value: entrepot?.location?.complement,
            error: "",
            isRequired: false,
            isDisabled: false,
          },
          postalCode: {
            value: entrepot?.location?.cp,
            error: "",
            isRequired: true,
            isDisabled: false,
          },
          city: {
            value: entrepot?.location?.city,
            error: "",
            isRequired: true,
            isDisabled: false,
          },
          country: {
            value: entrepot?.location?.country || "France",
            error: "",
            isRequired: true,
            isDisabled: false,
          },
          colisage: {
            value:
              (op?.shipType === shipType && op?.colisage) || colisage || "",
            error: "",
            isRequired: false,
            isDisabled: false,
          },
        });
        break;
      default:
        setContactShipping({
          shipEntrepotName: {
            value: "N/A",
            error: "",
            isRequired: false,
            isDisabled: true,
          },
          adress: {
            value:
              (op?.shipType === shipType && op?.shipAdress) ||
              edb?.dockyard?.address ||
              "",
            error: "",
            isRequired: true,
            isDisabled: false,
          },
          complementAdress: {
            value:
              (op?.shipType === shipType && op?.shipAdress2) ||
              edb?.dockyard?.address2 ||
              "N/A",
            error: "",
            isRequired: false,
            isDisabled: false,
          },
          postalCode: {
            value:
              (op?.shipType === shipType && op?.shipPostalCode) ||
              edb?.dockyard?.postalCode ||
              "",
            error: "",
            isRequired: true,
            isDisabled: false,
          },
          city: {
            value:
              (op?.shipType === shipType && op?.shipCity) ||
              edb?.dockyard?.city ||
              "",
            error: "",
            isRequired: true,
            isDisabled: false,
          },
          country: {
            value:
              (op?.shipType === shipType && op?.shipCountry) ||
              edb?.dockyard?.country ||
              "France",
            error: "",
            isRequired: true,
            isDisabled: false,
          },
          colisage: {
            value:
              (op?.shipType === shipType && op?.colisage) || colisage || "",
            error: "",
            isRequired: false,
            isDisabled: false,
          },
        });
        break;
    }
  }, [
    colisage,
    entrepot,
    edb?.dispositionType,
    edb?.dispositionAdress,
    edb?.dispositionAdress2,
    edb?.dispositionCity,
    edb?.dispositionCountry,
    edb?.dispositionPostalCode,
    edb?.dockyard?.address,
    edb?.dockyard?.address2,
    edb?.dockyard?.city,
    edb?.dockyard?.country,
    edb?.dockyard?.postalCode,
    edb?.entrepotName,
    op?.colisage,
    op?.shipAdress,
    op?.shipAdress2,
    op?.shipCity,
    op?.shipCountry,
    op?.shipEntrepotName,
    op?.shipPostalCode,
    op?.shipType,
    shipType,
    op?.shipPlace,
    edb?.dispositionPlace,
    OPDeliveryData,
  ]);

  // Check if the OP is part of a shipped shipment by checking status
  // This is computed after all hooks to avoid conditional hook detection
  const isOpInShippedShipment =
    // For initialShippingRecord which is a ShippingRecord, use the ShippingRecordStatus enum
    initialShippingRecord?.status === "SHIPPED" ||
    // For OpInAShippedShipment which is a Shipment, use the ShipmentStatus enum
    OpInAShippedShipment?.status === ShipmentStatus.SHIPPED;

  const setDeliveryDataDispositionAddress = (
    deliveryAddress: ShippingAddress
  ) => {
    switch (shipType) {
      case DispositionType.OTHER_PLACE:
        setOPDeliveryData({
          ...OPDeliveryData,
          otherPlace: {
            ...OPDeliveryData.otherPlace,
            address: {
              ...OPDeliveryData.otherPlace.address,
              ...deliveryAddress,
            },
          },
        });
        break;
      case DispositionType.SUBCONTRACTED_WAREHOUSE:
        setOPDeliveryData({
          ...OPDeliveryData,
          subcontratedWarehouse: {
            ...OPDeliveryData.subcontratedWarehouse,
            address: {
              ...OPDeliveryData.subcontratedWarehouse.address,
              ...deliveryAddress,
            },
          },
        });
        break;
      case DispositionType.NEAREST_WAREHOUSE:
        setOPDeliveryData({
          ...OPDeliveryData,
          nearestWarehouse: {
            ...OPDeliveryData.nearestWarehouse,
            address: {
              ...OPDeliveryData.nearestWarehouse.address,
              ...deliveryAddress,
            },
          },
        });
        break;
      default:
        break;
    }
  };

  const updateProductQty = (product: any, value: number) => {
    const indexProduct = products.findIndex(
      (x: any) => x.relatedProduct._id === product.relatedProduct._id
    );
    handleChangeProductOP(indexOP, indexProduct, value, entrepot);
  };

  const updateOp = (value: string, key: string, sendData = false) => {
    const newOp = { ...op } as any;
    newOp[key] = value;
    handleChangeOP(indexOP, newOp, sendData, false);
  };

  const renderData = () =>
    products.map((product) => [
      {
        value: product.relatedProduct.reference,
        customCellClass: classes.cellClass,
      },
      {
        value: product.relatedProduct.designation,
        customCellClass: classes.cellClass,
      },
      {
        value: entrepot.name,
        customCellClass: classes.cellClass,
      },
      {
        value: (
          <div className={classes.productQty}>
            <JTooltip
              title={
                !!product.receptionReferenceId &&
                "Pour changer cette quantité, veuillez retirer la référence de l'OP."
              }
            >
              <InputGrpNumber
                debounced
                product={product}
                disableInput={
                  isDisabled || status > 0 || !!product.receptionReferenceId
                }
                alsoOnChange={(val) => {
                  updateProductQty(product, val);
                }}
              />
            </JTooltip>
          </div>
        ),
        customCellClass: classes.cellClass,
      },
      {
        value: (
          <PopperButtons
            disabled={isDisabled || status > PreparationOrderStatus.CREATED}
            isProductDeletable={isProductDeletableFromEDB(product)}
            onDeleteFromOP={() => handleCancelOPItems(product._id, indexOP)}
            onDeleteFromEdb={() =>
              handleopenPopinDeleteProduct(
                product._id,
                product.relatedProduct._id
              )
            }
          />
        ),
        customCellClass: classes.cellActionClass,
      },
    ]);

  const tHead = [
    {
      id: "référence",
      label: "référence",
      minWidth: 50,
      style: {
        background: "#FFF",
      },
    },
    {
      id: "désignation",
      label: "désignation",
      minWidth: 150,
      style: {
        background: "#FFF",
      },
    },
    {
      id: "entrepot",
      label: "entrepôt de préparation",
      minWidth: 75,
      style: {
        background: "#FFF",
      },
    },
    {
      id: "qty",
      label: "quantité à préparer",
      minWidth: 150,
      style: {
        background: "#FFF",
      },
    },
    {
      id: "action",
      label: "action",
      minWidth: 75,
      style: {
        background: "#FFF",
        width: 56,
      },
    },
  ];

  // for cancel OP popin
  const handleCancelPopinOP = () => {
    if (cancelOpDisabled) return;
    setOpenPopinCancelOp(true);
  };

  const handleCancelPopinCancelOP = () => {
    setOpenPopinCancelOp(false);
  };

  const handleValidatePopinCancelOP = () => {
    setOpenPopinCancelOp(false);
    onCancelOP(_id);
  };

  // for validate OP popin
  const handleValidatePopinOP = () => {
    setOpenPopinValidateOp(true);
  };

  const handleCancelPopinValidateOP = () => {
    setOpenPopinValidateOp(false);
  };

  const isProductDeletableFromEDB = (product: {
    receptionReferenceId: number;
    relatedProduct: { _id: string };
  }) => {
    const {
      receptionReferenceId,
      relatedProduct: { _id: relatedProductId },
    } = product;
    if (receptionReferenceId) return false;

    // Check if product is not in another OP with status > 0 (pending)
    return opList
      .filter((otherOp) => otherOp._id !== op._id)
      .every((otherOp) => {
        const { products } = otherOp;
        const index = products.findIndex(
          (p) => p.relatedProduct._id === relatedProductId
        );
        return (
          index === -1 || otherOp.status === PreparationOrderStatus.CREATED
        );
      });
  };

  const handleValidatePopinValidateOP = () => {
    setOpenPopinValidateOp(false);
    // onValidateOP(_id, );
    const newOp = { ...op } as any;
    newOp.status = PreparationOrderStatus.IN_PROGRESS;
    delete newOp.statusHistory;
    handleChangeOP(indexOP, newOp, true, true);
  };

  const handleopenPopinDeleteProduct = (
    idOpItem: string,
    idProduct: string
  ) => {
    setInfosDeleteProductEdb({
      idOpItem,
      idProduct,
    });
    setOpenPopinDeleteProduct(true);
  };

  const onDeleteProductEdb = () => {
    setOpenPopinDeleteProduct(false);
    onDeleteItemFromEdb(infosDeleteProductEdb.idProduct, op.edb._id);
  };

  const handleChangeSTep3Form = (
    key: string,
    value: string,
    data: any,
    setData: any,
    isRequired = false,
    _regex?: string,
    errorMessage?: string
  ) => {
    const newData = { ...data };
    const rgx = new RegExp(_regex || "");
    if (!value && isRequired) {
      newData[key].error = "Veuillez remplir ce champ";
      newData[key].value = "";
    } else if (rgx.test(value) || (!isRequired && value === "")) {
      newData[key].value = value;
      newData[key].error = "";
    } else {
      newData[key].error = errorMessage;
    }
    setData(newData);
  };

  const checkShipping = () => {
    handleChangeSTep3Form(
      "shipEntrepotName",
      contactShipping.shipEntrepotName.value,
      contactShipping,
      setContactShipping,
      contactShipping.shipEntrepotName.isRequired
    );
    handleChangeSTep3Form(
      "adress",
      contactShipping.adress.value,
      contactShipping,
      setContactShipping,
      contactShipping.adress.isRequired
    );
    handleChangeSTep3Form(
      "complementAdress",
      contactShipping.complementAdress.value,
      contactShipping,
      setContactShipping,
      contactShipping.complementAdress.isRequired
    );
    handleChangeSTep3Form(
      "city",
      contactShipping.city.value,
      contactShipping,
      setContactShipping,
      contactShipping.city.isRequired
    );
    handleChangeSTep3Form(
      "country",
      contactShipping.country.value,
      contactShipping,
      setContactShipping,
      contactShipping.country.isRequired
    );
    handleChangeSTep3Form(
      "postalCode",
      contactShipping.postalCode.value,
      contactShipping,
      setContactShipping,
      contactShipping.postalCode.isRequired
    );
    handleChangeSTep3Form(
      "colisage",
      contactShipping.colisage.value,
      contactShipping,
      setContactShipping,
      contactShipping.colisage.isRequired
    );

    const keys = Object.keys(contactShipping);
    let resp = true;

    keys.map((k: string) => {
      if (contactShipping[k].error !== "") {
        resp = false;
      }
    });
    return resp;
  };

  const saveShipping = () => {
    const isValid = checkShipping();
    if (isValid) {
      setShowForm(false);
      setEditEntrepot(false);
      const newOp = { ...op } as any;
      delete newOp.statusHistory;
      newOp.shipEntrepotName = contactShipping.shipEntrepotName.value;
      newOp.shipAdress = contactShipping.adress.value;
      newOp.shipAdress2 =
        contactShipping.complementAdress.value !== "N/A"
          ? contactShipping.complementAdress.value
          : "";
      newOp.shipCity = contactShipping.city.value;
      newOp.shipCountry = contactShipping.country.value;
      newOp.shipPostalCode = contactShipping.postalCode.value;
      newOp.shipType = shipType;
      newOp.colisage = contactShipping.colisage.value;
      newOp.shipPlace = contactShipping.shipPlace?.value;

      // Persist per-shipType delivery data snapshots in JSON fields
      const updatedOPDeliveryData: OPDeliveryDataType = {
        ...OPDeliveryData,
      };

      const commonAddress = {
        adress: contactShipping.adress.value,
        city: contactShipping.city.value,
        complementAdress:
          contactShipping.complementAdress.value === "N/A"
            ? ""
            : contactShipping.complementAdress.value,
        country: contactShipping.country.value,
        postalCode: contactShipping.postalCode.value,
      };

      if (shipType === DispositionType.NEAREST_WAREHOUSE) {
        updatedOPDeliveryData.nearestWarehouse = {
          address: commonAddress,
          colisage: contactShipping.colisage.value,
          shipEntrepotName: contactShipping.shipEntrepotName.value,
          shipPlace: contactShipping.shipPlace?.value || "",
        };
      } else if (shipType === DispositionType.SUBCONTRACTED_WAREHOUSE) {
        updatedOPDeliveryData.subcontratedWarehouse = {
          address: commonAddress,
          colisage: contactShipping.colisage.value,
          shipEntrepotName: contactShipping.shipEntrepotName.value,
          shipPlace: contactShipping.shipPlace?.value || "",
        };
      } else if (shipType === DispositionType.OTHER_PLACE) {
        updatedOPDeliveryData.otherPlace = {
          address: commonAddress,
          colisage: contactShipping.colisage.value,
          shipEntrepotName: "",
          shipPlace: contactShipping.shipPlace?.value || "",
        };
      }

      setOPDeliveryData(updatedOPDeliveryData);

      newOp.nearestShipWarehouse = {
        name: updatedOPDeliveryData.nearestWarehouse.shipEntrepotName || "",
        postalCode:
          updatedOPDeliveryData.nearestWarehouse.address.postalCode || "",
        place: updatedOPDeliveryData.nearestWarehouse.shipPlace || "",
        city: updatedOPDeliveryData.nearestWarehouse.address.city || "",
        adress: updatedOPDeliveryData.nearestWarehouse.address.adress || "",
        complementAdress:
          updatedOPDeliveryData.nearestWarehouse.address.complementAdress || "",
        country: updatedOPDeliveryData.nearestWarehouse.address.country || "",
      };

      newOp.otherShipWarehouse = {
        name: updatedOPDeliveryData.otherPlace.shipEntrepotName || "",
        postalCode: updatedOPDeliveryData.otherPlace.address.postalCode || "",
        place: updatedOPDeliveryData.otherPlace.shipPlace || "",
        city: updatedOPDeliveryData.otherPlace.address.city || "",
        adress: updatedOPDeliveryData.otherPlace.address.adress || "",
        complementAdress:
          updatedOPDeliveryData.otherPlace.address.complementAdress || "",
        country: updatedOPDeliveryData.otherPlace.address.country || "",
      };

      newOp.subcontractorShipWarehouse = {
        name:
          updatedOPDeliveryData.subcontratedWarehouse.shipEntrepotName || "",
        postalCode:
          updatedOPDeliveryData.subcontratedWarehouse.address.postalCode || "",
        place: updatedOPDeliveryData.subcontratedWarehouse.shipPlace || "",
        city: updatedOPDeliveryData.subcontratedWarehouse.address.city || "",
        adress:
          updatedOPDeliveryData.subcontratedWarehouse.address.adress || "",
        complementAdress:
          updatedOPDeliveryData.subcontratedWarehouse.address
            .complementAdress || "",
        country:
          updatedOPDeliveryData.subcontratedWarehouse.address.country || "",
      };

      handleChangeOP(indexOP, newOp, true);
    } else {
      console.error("CheckShipping error");
    }
  };

  const handleExportXls = () => {
    const dataToExport: any[][] = [EXPORT_OP_XLS_HEAD];
    op.products.forEach((product) => {
      dataToExport.push([
        op?.entrepot?.name,
        op?.code,
        op?.reference,
        product?.relatedProduct?.reference,
        product?.relatedProduct?.designation,
        product?.qty,
      ]);
    });
    const ws = XLSX.utils.aoa_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Export");
    XLSX.writeFile(
      wb,
      `liste des préparations ${
        op?.entrepot?.name ?? ""
      } ${formatDateTime()}.xlsx`
    );
  };
  const handleChangeWarehouse = (warehouseName: string) => {
    const warehouse = (warehouses?.data || []).find(
      (warehouse: any) =>
        warehouse.name === warehouseName &&
        (warehouse.companies.find((w: any) => w._id === edb?.company?._id) ||
          warehouse.parentCompany === edb?.company?._id)
    );
    handleChangeSTep3Form(
      "shipEntrepotName",
      warehouseName,
      contactShipping,
      setContactShipping,
      contactShipping.shipEntrepotName.isRequired
    );
    handleChangeSTep3Form(
      "adress",
      warehouse?.location?.adress,
      contactShipping,
      setContactShipping,
      contactShipping.adress.isRequired
    );
    handleChangeSTep3Form(
      "city",
      warehouse?.location?.city,
      contactShipping,
      setContactShipping,
      contactShipping.city.isRequired
    );
    handleChangeSTep3Form(
      "postalCode",
      warehouse?.location?.cp,
      contactShipping,
      setContactShipping,
      contactShipping.postalCode.isRequired
    );
    handleChangeSTep3Form(
      "country",
      warehouse?.location?.country,
      contactShipping,
      setContactShipping,
      contactShipping.country.isRequired
    );
    handleChangeSTep3Form(
      "complementAdress",
      warehouse?.location?.complement,
      contactShipping,
      setContactShipping,
      contactShipping.complementAdress.isRequired
    );
  };

  const handleChangeRelayPoint = (companyId: any) => {
    const company = relayAgencyOptions?.find(
      (relayAgency: any) =>
        relayAgency._id === companyId || relayAgency?.name === companyId
    );
    handleChangeSTep3Form(
      "shipEntrepotName",
      company?.name,
      contactShipping,
      setContactShipping,
      contactShipping.shipEntrepotName.isRequired
    );
    handleChangeSTep3Form(
      "adress",
      company?.adress,
      contactShipping,
      setContactShipping,
      contactShipping.adress.isRequired
    );
    handleChangeSTep3Form(
      "city",
      company?.city,
      contactShipping,
      setContactShipping,
      contactShipping.city.isRequired
    );
    handleChangeSTep3Form(
      "postalCode",
      company?.postalCode,
      contactShipping,
      setContactShipping,
      contactShipping.postalCode.isRequired
    );
    handleChangeSTep3Form(
      "country",
      company?.country,
      contactShipping,
      setContactShipping,
      contactShipping.country.isRequired
    );
    handleChangeSTep3Form(
      "complementAdress",
      company?.complementAdress,
      contactShipping,
      setContactShipping,
      contactShipping.complementAdress.isRequired
    );
  };

  const getShipWarehouseName = () => {
    if (shipType === DispositionType.NEAREST_WAREHOUSE) {
      const company = relayAgencyOptions?.find(
        (relayAgency: any) =>
          shipEntrepotName === relayAgency._id ||
          shipEntrepotName === relayAgency?.name
      );
      return company
        ? company.name
        : shipEntrepotName !== ""
          ? shipEntrepotName
          : "N/A";
    }

    if (shipType === DispositionType.OTHER_PLACE) {
      return contactShipping.shipPlace?.value;
    }

    if (shipType === DispositionType.COLLECTED) {
      return entrepot?.name;
    }

    return shipEntrepotName !== "" ? shipEntrepotName : "N/A";
  };

  const exportStockItPreparation = async () => {
    const body = {
      opId: op._id,
    };
    const response = await exportData(token, body, "/ops/stockIt");
    const filename = response.headers
      .get("Content-Disposition")
      .split("filename=")[1];
    downloadFile(response, filename, "xlsx", true);
  };

  return (
    <div
      className={clsx(
        classes.op,
        status === PreparationOrderStatus.CANCELLED && classes.cancelled
      )}
    >
      <div className={classes.opHeader}>
        <div className={classes.opTitle}>
          <div style={{ display: "flex", marginBottom: 10 }}>
            <Text type="h3" className={classes.code}>
              {code}
            </Text>
            {status === PreparationOrderStatus.CANCELLED ? (
              <Badge color="red">Annulé</Badge>
            ) : status > PreparationOrderStatus.CREATED ? (
              <Badge color="green">Validé</Badge>
            ) : null}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            {status >= PreparationOrderStatus.CREATED &&
              entrepot?.name.indexOf(VFSOLUTIONS) !== -1 && (
                <Button
                  className={classes.stockItButton}
                  onClick={exportStockItPreparation}
                  style={{
                    minWidth: "initial",
                    marginRight: "8px",
                  }}
                  type="button"
                  color="primary"
                >
                  <Upload2LineIcon />
                  Préparation Stock-it
                </Button>
              )}
            <UIButton
              className={classes.exportXlsButton}
              onClick={handleExportXls}
            >
              <Upload2LineIcon />
              Exporter en XLS
            </UIButton>
          </div>
        </div>
        <div className={classes.opTitleRefs}>
          <TextField
            value={reference || ""}
            variant="outlined"
            placeholder="Référence de l'OP"
            disabled={status > 0}
            className={classes.refOp}
            onChange={(e: any) => updateOp(e.target.value, "reference")}
            onBlur={(e: any) => updateOp(e.target.value, "reference", true)}
          />

          {status !== PreparationOrderStatus.CANCELLED && (
            <JTooltip
              title={
                (isDisabled && EDB_IS_DISABLED_MSG) ||
                (OpInAShippedShipment !== null &&
                  !!OpInAShippedShipment.id &&
                  IS_OP_IN_SHIPPED_SHIPMENT_MSG(
                    OpInAShippedShipment?.shippingRecord?.code
                  ))
              }
            >
              <TextField
                style={{ backgroundColor: "white" }}
                value={refPreparationEntrepot || ""}
                variant="outlined"
                placeholder="Réf préparation entrepôt"
                disabled={
                  isDisabled ||
                  (OpInAShippedShipment !== null && !!OpInAShippedShipment.id)
                }
                className={classes.refEntrepot}
                inputProps={{ maxLength: 35 }}
                onChange={(e: any) => {
                  updateOp(e.target.value, "refPreparationEntrepot");
                  if (status > PreparationOrderStatus.CREATED) {
                    setEditEntrepot(true);
                    setRefEntrepotValue(e.target.value);
                  }
                }}
                onBlur={(e: any) => {
                  if (status > PreparationOrderStatus.CREATED) return;
                  updateOp(e.target.value, "refPreparationEntrepot", true);
                }}
              />
            </JTooltip>
          )}

          {editEntrepot && (
            <div className={classes.saveLivraison}>
              <Button
                className={classes.validateOP}
                onClick={() => {
                  updateOp(refEntrepotValue, "refPreparationEntrepot", true);
                  setEditEntrepot(false);
                }}
              >
                Enregistrer
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className={classes.opContent}>
        <Table
          className={classes.products}
          rowsPerPage={8}
          thead={tHead}
          bodyData={renderData()}
        />
      </div>
      {status <= PreparationOrderStatus.PREPARED && (
        <div className={classes.opAction}>
          <JTooltip title={cancelOpTooltipTitle}>
            <Button
              color="secondary"
              disabled={cancelOpDisabled}
              onClick={() => handleCancelPopinOP()}
            >
              Annuler
            </Button>
          </JTooltip>
          {status === PreparationOrderStatus.CREATED && (
            <JTooltip title={isDisabled && EDB_IS_DISABLED_MSG}>
              <Button
                className={classes.validateOP}
                disabled={isDisabled}
                onClick={() => handleValidatePopinOP()}
              >
                Valider
              </Button>
            </JTooltip>
          )}
        </div>
      )}
      {status >= PreparationOrderStatus.CREATED && !showForm && (
        <div className={classes.deliverySection}>
          <div style={{ display: "flex", marginBottom: 10 }}>
            <Text type="h3" className={classes.code}>
              Livraison
            </Text>
          </div>
          <div className={classes.infoLivraison}>
            <span>{shipType !== "" ? shipType : "N/A"}</span>
            <Divider
              orientation="vertical"
              flexItem
              className={classes.divider}
            />
            <span>{getShipWarehouseName()}</span>
            <Divider
              orientation="vertical"
              flexItem
              className={classes.divider}
            />
            {shipType !== "" &&
              contactShipping &&
              contactShipping?.adress?.value !== "N/A" && (
                <span>
                  {`${contactShipping?.adress?.value}, ${
                    contactShipping?.complementAdress?.value !== "N/A" &&
                    contactShipping?.complementAdress?.value !== ""
                      ? `${contactShipping?.complementAdress?.value} `
                      : ""
                  }${contactShipping?.postalCode?.value} ${
                    contactShipping?.city?.value
                  } ${contactShipping?.country?.value}`}
                </span>
              )}
            {status >= PreparationOrderStatus.CREATED &&
              status < PreparationOrderStatus.SHIPPED &&
              (edb.preparationType !== "Préparation & restockage" ? (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  {!isOpInShippedShipment && (
                    <UIButton
                      className={classes.editButton}
                      onClick={() => setShowForm(true)}
                    >
                      <Edit2LineIcon />
                    </UIButton>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  Les instructions de livraison n&apos;ont pas été renseignées
                </div>
              ))}
          </div>
          {status > PreparationOrderStatus.CREATED &&
            status <= PreparationOrderStatus.COLLECTED && (
              <div style={{ marginTop: 16 }}>
                <div className={classes.parcelInfosHeader}>
                  <Text type="h3" className={classes.code}>
                    Colisage
                  </Text>
                  <div>
                    {(op.status === PreparationOrderStatus.PREPARED ||
                      op.status === PreparationOrderStatus.IN_PROGRESS) && (
                      <Box flex={1}>
                        <UIButton
                          className={classes.editButton}
                          onClick={() => toggleParcelDrawer(true)}
                        >
                          <Edit2LineIcon />
                        </UIButton>
                      </Box>
                    )}
                    {parcel && Object.keys(parcel).length > 0 && (
                      <UIButton
                        className={classes.editButton}
                        onClick={() => toggleParcelDetails(!showParcelDetails)}
                      >
                        {showParcelDetails ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </UIButton>
                    )}
                  </div>
                </div>
                <div className={classes.parcelInfosContent}>
                  {parcel && Object.keys(parcel).length > 0 ? (
                    <ParcelTable
                      parcel={parcel}
                      showDetailsTable={showParcelDetails}
                    />
                  ) : (
                    "Veuillez ajouter un colisage"
                  )}
                </div>
              </div>
            )}
        </div>
      )}
      {status >= PreparationOrderStatus.CREATED &&
        status < PreparationOrderStatus.SHIPPED &&
        showForm && (
          <div className={classes.formSection}>
            <div style={{ display: "flex", marginBottom: 10 }}>
              <Text type="h3" className={classes.code}>
                Livraison
              </Text>
            </div>
            <div className={classes.contentLivraison}>
              <div className={classes.radioButton}>
                <RadioGroup
                  row
                  aria-label="shipType"
                  name="shipType"
                  defaultValue={shipType}
                  onChange={(e: any) => setShipType(e.target.value)}
                >
                  <FormControlLabel
                    value={DispositionType.ON_DOCKYARD}
                    control={<Radio color="primary" />}
                    label="Chantier"
                  />
                  <FormControlLabel
                    value={DispositionType.NEAREST_WAREHOUSE}
                    control={<Radio color="primary" />}
                    label="Entrepôt le plus proche"
                  />
                  <FormControlLabel
                    value={DispositionType.SUBCONTRACTED_WAREHOUSE}
                    control={<Radio color="primary" />}
                    label="Entrepôt sous-traitant"
                  />
                  <FormControlLabel
                    value={DispositionType.OTHER_PLACE}
                    control={<Radio color="primary" />}
                    label="Autre lieu"
                  />
                  <FormControlLabel
                    value={DispositionType.COLLECTED}
                    control={<Radio color="primary" />}
                    label="Collecte"
                  />
                </RadioGroup>
              </div>
              <div>
                <div className={clsx(classes.row, classes.flex)}>
                  {shipType === DispositionType.ON_DOCKYARD && (
                    <div>
                      <TextField
                        className={clsx(classes.postalCode, classes.textField)}
                        variant="outlined"
                        label="Nom de chantier"
                        value={edb.dockyard?.name}
                        disabled
                      />
                      <TextField
                        className={clsx(
                          classes.textFieldSpacer,
                          classes.textField
                        )}
                        variant="outlined"
                        label="Code chantier"
                        value={edb.dockyard?.code}
                        disabled
                      />
                    </div>
                  )}
                  {shipType === DispositionType.OTHER_PLACE && (
                    <TextField
                      className={classes.postalCode}
                      variant="outlined"
                      label="Nom"
                      value={
                        contactShipping.shipPlace?.value ??
                        OPDeliveryData.otherPlace.shipPlace
                      }
                      onChange={(e: any) => {
                        handleChangeSTep3Form(
                          "shipPlace",
                          e.target.value,
                          contactShipping,
                          setContactShipping,
                          true
                        );
                        setOPDeliveryData({
                          ...OPDeliveryData,
                          otherPlace: {
                            ...OPDeliveryData.otherPlace,
                            shipPlace: e.target.value,
                          },
                        });
                      }}
                      disabled={contactShipping.shipEntrepotName.isDisabled}
                    />
                  )}
                  {shipType === DispositionType.SUBCONTRACTED_WAREHOUSE &&
                    (warehouseLoading ? (
                      <Loader />
                    ) : (
                      <WarehouseSelect
                        className={classes.textField}
                        label="Nom"
                        valueKey="name"
                        onChange={handleChangeWarehouse}
                        value={contactShipping.shipEntrepotName.value}
                        style={{ width: "50%" }}
                        error={!!contactShipping.shipEntrepotName.error}
                        required
                        warehouseOptions={warehouses?.data || []}
                        disabled={
                          isDisabled ||
                          (OpInAShippedShipment !== null &&
                            !!OpInAShippedShipment.id)
                        }
                      />
                    ))}
                  {shipType === DispositionType.NEAREST_WAREHOUSE && (
                    <CompanySelect
                      className={classes.textField}
                      label="Liste des points relais"
                      companyTypes={[CompanyType.AGENCY]}
                      companyOptions={relayAgencyOptions}
                      onChange={handleChangeRelayPoint}
                      value={contactShipping.shipEntrepotName.value}
                      style={{ width: "50%" }}
                      error={!!contactShipping.shipEntrepotName.error}
                      valueKey="name"
                      minCharsToTrigerSearch={0}
                      rowsPerPage={99999}
                      required
                    />
                  )}
                  {shipType === DispositionType.COLLECTED && (
                    <div style={{ marginRight: 4, width: 300 }}>
                      <WarehouseSelector
                        label={
                          entrepot ? entrepot.name : "Entrepôt de préparation"
                        }
                        valueKey="name"
                        value={edb?.entrepotName || entrepot?.name || ""}
                        disabled
                        placeholder="Entrepôt de préparation"
                        onChange={() => {}}
                      />
                    </div>
                  )}
                </div>
                <div className={clsx(classes.row, classes.flex)}>
                  <AddressSelect
                    disabled={
                      contactShipping.adress.isDisabled ||
                      shipType !== DispositionType.OTHER_PLACE
                    }
                    error={contactShipping.adress.error}
                    required={contactShipping.adress.isRequired}
                    value={contactShipping.adress.value}
                    onChange={(data) => {
                      // Prevent changing value if it's the same (especially if it's empty)
                      if (contactShipping.adress.value === data) return;
                      if (Array.isArray(data)) {
                        let street = "";
                        const deliveryAddress: ShippingAddress = {};
                        data.forEach((elem: any) => {
                          if (elem.types.includes("postal_code")) {
                            handleChangeSTep3Form(
                              "postalCode",
                              elem.long_name,
                              contactShipping,
                              setContactShipping,
                              contactShipping.postalCode.isRequired
                            );
                            deliveryAddress.postalCode = elem.long_name;
                          }
                          if (elem.types.includes("country")) {
                            handleChangeSTep3Form(
                              "country",
                              elem.long_name,
                              contactShipping,
                              setContactShipping,
                              contactShipping.country.isRequired
                            );
                            deliveryAddress.country = elem.long_name;
                          }
                          if (elem.types.includes("locality")) {
                            handleChangeSTep3Form(
                              "city",
                              elem.long_name,
                              contactShipping,
                              setContactShipping,
                              contactShipping.city.isRequired
                            );
                            deliveryAddress.city = elem.long_name;
                          }
                          if (elem.types.includes("street_number")) {
                            if (street === "") {
                              street += elem.long_name;
                            } else {
                              street = `${elem.long_name}${street}`;
                            }
                          }
                          if (elem.types.includes("route")) {
                            street += ` ${elem.long_name}`;
                          }
                        });
                        handleChangeSTep3Form(
                          "adress",
                          street,
                          contactShipping,
                          setContactShipping,
                          contactShipping.adress.isRequired
                        );
                        deliveryAddress.adress = street;
                        setDeliveryDataDispositionAddress(deliveryAddress);
                      } else {
                        handleChangeSTep3Form(
                          "adress",
                          data,
                          contactShipping,
                          setContactShipping,
                          contactShipping.adress.isRequired
                        );
                        setDeliveryDataDispositionAddress({ adress: data });
                      }
                    }}
                  />
                </div>
                <div className={clsx(classes.row, classes.flex)}>
                  <TextField
                    className={classes.textField}
                    label="Complément d'adresse"
                    variant="outlined"
                    placeholder="Tapez le complément d'adresse"
                    FormHelperTextProps={{
                      className: classes.errorText,
                    }}
                    value={contactShipping.complementAdress.value}
                    helperText={contactShipping.complementAdress.error}
                    disabled={
                      contactShipping.complementAdress.isDisabled ||
                      shipType !== DispositionType.OTHER_PLACE
                    }
                    onChange={(e: any) => {
                      handleChangeSTep3Form(
                        "complementAdress",
                        e.target.value,
                        contactShipping,
                        setContactShipping,
                        contactShipping.complementAdress.isRequired
                      );
                      setDeliveryDataDispositionAddress({
                        complementAdress: e.target.value,
                      });
                    }}
                  />
                </div>
                <div className={clsx(classes.row, classes.flex)}>
                  <div className={clsx(classes.flex)}>
                    <TextField
                      className={clsx(classes.postalCode, classes.textField)}
                      label="Code postal"
                      variant="outlined"
                      placeholder="Tapez le code postal"
                      disabled={
                        contactShipping.postalCode.isDisabled ||
                        shipType !== DispositionType.OTHER_PLACE
                      }
                      required={contactShipping.postalCode.isRequired}
                      value={contactShipping.postalCode.value}
                      helperText={contactShipping.postalCode.error}
                      FormHelperTextProps={{
                        className: classes.errorText,
                      }}
                      onChange={(e: any) => {
                        handleChangeSTep3Form(
                          "postalCode",
                          e.target.value,
                          contactShipping,
                          setContactShipping,
                          contactShipping.postalCode.isRequired
                        );
                        setDeliveryDataDispositionAddress({
                          postalCode: e.target.value,
                        });
                      }}
                    />
                    <TextField
                      className={clsx(
                        classes.textFieldSpacer,
                        classes.textField
                      )}
                      label="Ville"
                      variant="outlined"
                      placeholder="Tapez la ville"
                      FormHelperTextProps={{
                        className: classes.errorText,
                      }}
                      required={contactShipping.city.isRequired}
                      value={contactShipping.city.value}
                      helperText={contactShipping.city.error}
                      disabled={
                        contactShipping.city.isDisabled ||
                        shipType !== DispositionType.OTHER_PLACE
                      }
                      onChange={(e: any) => {
                        handleChangeSTep3Form(
                          "city",
                          e.target.value,
                          contactShipping,
                          setContactShipping,
                          contactShipping.city.isRequired
                        );
                        setDeliveryDataDispositionAddress({
                          city: e.target.value,
                        });
                      }}
                    />
                    <TextField
                      className={classes.textField}
                      label="Pays"
                      variant="outlined"
                      placeholder="Tapez le pays"
                      FormHelperTextProps={{
                        className: classes.errorText,
                      }}
                      required={contactShipping.country.isRequired}
                      value={contactShipping.country.value}
                      helperText={contactShipping.country.error}
                      disabled={
                        contactShipping.country.isDisabled ||
                        shipType !== DispositionType.OTHER_PLACE
                      }
                      onChange={(e: any) => {
                        handleChangeSTep3Form(
                          "country",
                          e.target.value,
                          contactShipping,
                          setContactShipping,
                          contactShipping.country.isRequired
                        );
                        setDeliveryDataDispositionAddress({
                          country: e.target.value,
                        });
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className={classes.saveLivraison}>
              <Button
                className={classes.validateOP}
                onClick={() => saveShipping()}
              >
                Enregistrer
              </Button>
            </div>
          </div>
        )}

      <ParcelPopin
        isOpen={isParcelDrawerOpen}
        onClose={() => toggleParcelDrawer(false)}
        onCancel={() => toggleParcelDrawer(false)}
        parcel={parcel}
        createParcel={createParcel}
        updateParcel={updateParcel}
        deleteParcel={deleteParcel}
        subTitle={op?.reference}
        opStatus={op?.status}
      />

      <Modal
        open={openPopinCancelOp}
        onCancel={handleCancelPopinCancelOP}
        onValidate={handleValidatePopinCancelOP}
        textCancel="Ne pas annuler"
        textValidate="Annuler"
      >
        <div className={jclasses.jDialogContent}>
          <Text>
            Attention. Vous êtes sur le point d&apos;annuler l&apos;OP.
            L&apos;ensemble des produits concernés seront basculés sur le
            tableau “Suivi de l&apos;expression de besoin”.
            <br />
            Êtes-vous sûr de vouloir continuer ?
          </Text>
        </div>
      </Modal>

      <Modal
        open={openPopinValidateOp}
        onCancel={handleCancelPopinValidateOP}
        onValidate={handleValidatePopinValidateOP}
        textCancel="Annuler"
        textValidate="Valider l'OP"
      >
        <div className={jclasses.jDialogContent}>
          <Text>
            Attention. Vous êtes sur le point de valider l&apos;OP, vous ne
            pourrez donc plus en modifier le contenu.
            <br />
            Êtes-vous sûr de vouloir continuer ?
          </Text>
        </div>
      </Modal>

      <Modal
        textValidate="Supprimer"
        textCancel="Annuler"
        open={openPopinDeleteProduct}
        onValidate={onDeleteProductEdb}
        onCancel={() => setOpenPopinDeleteProduct(false)}
      >
        <div className={jclasses.jDialogContent}>
          <Text>
            Attention
            <br />
            Vous êtes sur le point de supprimer définitivement ce produit de
            l&apos;EDB.
            <br />
            Êtes-vous sûr de vouloir continuer ?
          </Text>
        </div>
      </Modal>
    </div>
  );
}

export default OP;

const useStyles = createUseStyles(() => ({
  op: {
    background: theme.color.gray1,
    padding: 16,
    borderRadius: 3,
    margin: "15px 0",
  },
  opHeader: {
    marginBottom: 10,
  },
  opContent: {
    marginBottom: 15,
  },
  opTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 14,
  },
  opTitleRefs: {
    display: "flex",
    alignItems: "center",
  },
  products: {
    boxShadow: "none",
    padding: 0,
  },
  code: {
    margin: "0 15px 0 0 !important",
    fontWeight: "bold",
    fontSize: 14,
  },
  cellClass: {
    background: theme.color.gray1,
  },
  cellActionClass: {
    background: theme.color.gray1,
    justifyContent: "center",
    height: "100%",
  },
  productQty: {
    width: 160,
  },
  refOp: {
    "& input": {
      padding: "10px 14px",
    },
    "& .MuiInputLabel-outlined": {
      transform: "translate(14px, 12px) scale(1)",
    },
    marginRight: "15px !important",
  },
  refEntrepot: {
    "& input": {
      padding: "10px 14px",
    },
    "& .MuiInputLabel-outlined": {
      transform: "translate(14px, 12px) scale(1)",
    },
    borderLeft: `solid 1px ${theme.color.gray2}`,
    paddingLeft: 15,
  },
  opAction: {
    display: "flex",
    justifyContent: "flex-end",
  },
  validateOP: {
    marginLeft: "15px !important",
  },
  contentLivraison: {},
  radioButton: {
    "& .MuiFormControlLabel-label": {
      fontSize: "14px !important",
    },
    marginBottom: 12,
  },
  flex: {
    display: "flex",
    flex: 1,
  },
  row: {
    marginBottom: 12,
  },
  textField: {
    flex: 1,
    background: theme.color.white,
  },
  textFieldSpacer: {
    margin: "0 10px !important",
  },
  errorText: {
    color: `${theme.color.red} !important`,
  },
  postalCode: {
    flex: 0.5,
    background: theme.color.white,
  },
  saveLivraison: {
    display: "flex",
    justifyContent: "flex-end",
  },
  infoLivraison: {
    display: "flex",
    alignItems: "center",
  },
  parcelInfosHeader: {
    marginTop: 10,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  parcelInfosContent: {
    marginTop: 10,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  divider: {
    margin: "0 15px !important",
  },
  // Nouveau style pour le bouton "Exporter en XLS"
  exportXlsButton: {
    color: "#000000 !important",
    backgroundColor: "transparent !important",
    textTransform: "none !important",
    fontSize: "14px !important",
    border: "none !important",
    boxShadow: "none !important",
    transition: "background-color 0.2s ease !important",
    "&:hover": {
      backgroundColor: "#f5f5f5 !important",
      color: "#000000 !important",
      boxShadow: "none !important",
    },
    "&:disabled": {
      color: "#cccccc !important",
      backgroundColor: "transparent !important",
    },
    "& svg": {
      color: "inherit !important",
    },
  },
  // Style pour les boutons d'édition
  editButton: {
    border: "none !important",
    color: "rgba(0, 0, 0) !important",
    minWidth: "24px !important",
    padding: "8px !important",
    backgroundColor: "transparent !important",
    boxShadow: "none !important",
    "&:hover": {
      color: "rgba(0, 0, 0) !important",
      backgroundColor: "rgba(0, 0, 0, 0.04) !important",
      boxShadow: "none !important",
    },
    "&:disabled": {
      color: "rgba(0, 0, 0, 0.26) !important",
      backgroundColor: "transparent !important",
    },
    "& svg": {
      color: "inherit !important",
    },
  },
  cancelled: {
    "& $opTitleRefs": {
      opacity: 0.5,
      pointerEvents: "none",
    },
    "& $opContent": {
      opacity: 0.5,
      pointerEvents: "none",
    },
    "& $opAction": {
      opacity: 0.5,
      pointerEvents: "none",
    },
    "& $deliverySection": {
      opacity: 0.5,
      pointerEvents: "none",
    },
    "& $formSection": {
      opacity: 0.5,
      pointerEvents: "none",
    },
    "& $stockItButton": {
      opacity: 0.5,
      pointerEvents: "none",
    },
  },
  deliverySection: {},
  formSection: {},
  stockItButton: {},
}));
